import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, AlertTriangle, Shield, User, Edit2, Save, X } from "lucide-react";
import { toast } from "sonner";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

export default function EmergencySection({ token, rideId = null }) {
  const { t } = useTranslation();
  const [emergencyContact, setEmergencyContact] = useState({ name: "", phone: "" });
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ name: "", phone: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchEmergencyContact();
  }, []);

  const fetchEmergencyContact = async () => {
    try {
      const res = await safeFetch(`${API}/emergency-contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmergencyContact({
          name: data.emergency_contact_name || "",
          phone: data.emergency_contact_phone || ""
        });
        setEditData({
          name: data.emergency_contact_name || "",
          phone: data.emergency_contact_phone || ""
        });
      }
    } catch (err) {
      console.error("Failed to fetch emergency contact:", err);
    }
  };

  const handleSaveContact = async () => {
    try {
      const res = await safeFetch(`${API}/emergency-contacts`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          emergency_contact_name: editData.name,
          emergency_contact_phone: editData.phone
        })
      });
      if (res.ok) {
        setEmergencyContact(editData);
        setEditing(false);
        toast.success("Emergency contact updated");
      }
    } catch (err) {
      toast.error("Failed to update contact");
    }
  };

  const handleEmergencyAlert = async () => {
    if (!confirm("Are you sure you want to send an emergency alert? This will notify our support team immediately.")) {
      return;
    }

    setSending(true);
    try {
      // Try to get location
      let location = "Unknown";
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          location = `Lat: ${pos.coords.latitude.toFixed(6)}, Lng: ${pos.coords.longitude.toFixed(6)}`;
        } catch {
          location = "Location unavailable";
        }
      }

      const res = await safeFetch(`${API}/emergency/alert`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ride_id: rideId, location })
      });

      if (res.ok) {
        toast.success("Emergency alert sent! Our team has been notified.");
      } else {
        toast.error("Failed to send alert. Please call emergency services directly.");
      }
    } catch (err) {
      toast.error("Failed to send alert. Please call 112 directly.");
    }
    setSending(false);
  };

  const callEmergency = (number) => {
    window.location.href = `tel:${number}`;
  };

  return (
    <div className="space-y-6" data-testid="emergency-section">
      {/* Emergency Alert Button */}
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg text-red-800">Emergency Alert</h3>
            <p className="text-sm text-red-600 mt-1">
              Press this button to immediately alert our support team. Use only in genuine emergencies.
            </p>
            <Button
              data-testid="emergency-alert-btn"
              onClick={handleEmergencyAlert}
              disabled={sending}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3"
            >
              {sending ? "Sending Alert..." : "🚨 SEND EMERGENCY ALERT"}
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Dial Numbers */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <h3 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-forest" />
          Emergency Numbers
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="call-112"
            onClick={() => callEmergency("112")}
            className="flex items-center justify-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-4 rounded-xl transition-colors"
          >
            <Phone className="w-5 h-5" />
            <span>112 (Emergency)</span>
          </button>
          <button
            data-testid="call-police"
            onClick={() => callEmergency("100")}
            className="flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-4 rounded-xl transition-colors"
          >
            <Shield className="w-5 h-5" />
            <span>100 (Police)</span>
          </button>
          <button
            data-testid="call-ambulance"
            onClick={() => callEmergency("108")}
            className="flex items-center justify-center gap-2 bg-green-100 hover:bg-green-200 text-green-700 font-semibold py-4 rounded-xl transition-colors"
          >
            <Phone className="w-5 h-5" />
            <span>108 (Ambulance)</span>
          </button>
          <button
            data-testid="call-women"
            onClick={() => callEmergency("1091")}
            className="flex items-center justify-center gap-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold py-4 rounded-xl transition-colors"
          >
            <Phone className="w-5 h-5" />
            <span>1091 (Women)</span>
          </button>
        </div>
      </div>

      {/* Personal Emergency Contact */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5 text-forest" />
            Personal Emergency Contact
          </h3>
          {!editing && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="edit-emergency-contact"
              onClick={() => setEditing(true)}
            >
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-600">Contact Name</label>
              <Input
                data-testid="emergency-contact-name"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                placeholder="e.g., Mom, Dad, Spouse"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600">Phone Number</label>
              <Input
                data-testid="emergency-contact-phone"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                placeholder="+91 9876543210"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="save-emergency-contact"
                onClick={handleSaveContact}
                className="bg-forest hover:bg-forest-dark text-white"
              >
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setEditData(emergencyContact);
                }}
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {emergencyContact.name || emergencyContact.phone ? (
              <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                <div>
                  <p className="font-medium text-slate-900">{emergencyContact.name || "Contact"}</p>
                  <p className="text-sm text-slate-500">{emergencyContact.phone}</p>
                </div>
                {emergencyContact.phone && (
                  <Button
                    data-testid="call-emergency-contact"
                    onClick={() => callEmergency(emergencyContact.phone)}
                    className="bg-forest hover:bg-forest-dark text-white"
                  >
                    <Phone className="w-4 h-4 mr-1" /> Call
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">
                No emergency contact set. Add one for quick access during emergencies.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
